class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.imageSearchButtonSelector = 'button#searchbar-image-search.ryzo-image-search-button, button[aria-label="Upload an image for search"], button[aria-label="Search By Image"]';
    this.imageSearchTooltipText = 'Search By Image';
    this.imageSearchButtonObserver = null;
    this.searchBarContainer = this.input?.closest('.field') || this.input?.form || this;

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
      this.input.addEventListener('focus', this.queueImageSearchTooltipShow.bind(this));
      this.input.addEventListener('click', this.queueImageSearchTooltipShow.bind(this));
      this.input.addEventListener('blur', this.hideImageSearchTooltip.bind(this));

      this.searchBarContainer.addEventListener('mouseover', this.queueImageSearchTooltipShow.bind(this));
      this.searchBarContainer.addEventListener('mouseleave', this.handleSearchBarMouseLeave.bind(this));
    }
    this.initializeImageSearchButtonObserver();
  }

  initializeImageSearchButtonObserver() {
    this.primeVisibleImageSearchButton();

    if (this.imageSearchButtonObserver || !document.body) {
      return;
    }

    this.imageSearchButtonObserver = new MutationObserver(() => {
      this.primeVisibleImageSearchButton();
    });

    this.imageSearchButtonObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  primeVisibleImageSearchButton() {
    const imageSearchButton = this.getVisibleImageSearchButton();
    if (!imageSearchButton) {
      return;
    }

    if (imageSearchButton.getAttribute('data-ryzo-tooltip') !== this.imageSearchTooltipText) {
      imageSearchButton.setAttribute('data-ryzo-tooltip', this.imageSearchTooltipText);
    }
  }

  queueImageSearchTooltipShow() {
    this.showImageSearchTooltip(0);
  }

  getVisibleImageSearchButton() {
    const localButton = this.getLocalImageSearchButton();
    if (localButton) {
      return localButton;
    }

    const allButtons = Array.from(document.querySelectorAll(this.imageSearchButtonSelector));
    if (!allButtons.length) {
      return null;
    }

    // Prefer a visible button inside the same search form when available.
    const nearbyVisibleButton = allButtons.find(
      (button) => button.offsetParent !== null && this.contains(button)
    );
    if (nearbyVisibleButton) {
      return nearbyVisibleButton;
    }

    return allButtons.find((button) => button.offsetParent !== null) || null;
  }

  getLocalImageSearchButton() {
    const searchField = this.input?.closest('.field');
    if (!searchField) {
      return null;
    }

    const localButton = searchField.querySelector(this.imageSearchButtonSelector);
    return localButton && localButton.offsetParent !== null ? localButton : null;
  }

  showImageSearchTooltip(retryCount = 0) {
    const imageSearchButton = this.getVisibleImageSearchButton();
    if (!imageSearchButton) {
      if (retryCount < 20) {
        window.setTimeout(() => this.showImageSearchTooltip(retryCount + 1), 60);
      }
      return;
    }

    if (imageSearchButton.getAttribute('data-ryzo-tooltip') !== this.imageSearchTooltipText) {
      imageSearchButton.setAttribute('data-ryzo-tooltip', this.imageSearchTooltipText);
    }

    // Ryzo attaches hover listeners asynchronously; wait briefly until they are ready.
    if (imageSearchButton.getAttribute('data-listener-added') !== 'true') {
      if (retryCount < 20) {
        window.setTimeout(() => this.showImageSearchTooltip(retryCount + 1), 60);
      }
      return;
    }

    this.syncImageSearchButtonVisibility(imageSearchButton);

    imageSearchButton.classList.add('search-input-tooltip-visible');
  }

  hideImageSearchTooltip() {
    this.resetImageSearchButtonVisibility();

    const imageSearchButton = this.getVisibleImageSearchButton();
    if (!imageSearchButton) {
      return;
    }

    imageSearchButton.classList.remove('search-input-tooltip-visible');
  }

  handleSearchBarMouseLeave() {
    const imageSearchButton = this.getVisibleImageSearchButton();
    if (!imageSearchButton) {
      return;
    }

    const relatedTarget = this.input?.form?.ownerDocument?.activeElement;
    if (relatedTarget === this.input) {
      return;
    }

    imageSearchButton.classList.remove('search-input-tooltip-visible');
    this.resetImageSearchButtonVisibility();
  }

  syncImageSearchButtonVisibility(activeButton) {
    document.querySelectorAll(this.imageSearchButtonSelector).forEach((button) => {
      if (button === activeButton) {
        button.classList.remove('search-input-tooltip-hidden');
        return;
      }

      button.classList.add('search-input-tooltip-hidden');
      button.classList.remove('search-input-tooltip-visible');
    });
  }

  resetImageSearchButtonVisibility() {
    document.querySelectorAll(this.imageSearchButtonSelector).forEach((button) => {
      button.classList.remove('search-input-tooltip-hidden');
    });
  }

  onChange() {
    return;
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
    }
  }
}

customElements.define('search-form', SearchForm);
